import {
  createContext,
  useState
} from 'react'
import {
  erase,
  get,
  set
} from 'browser-cookies'
import type { ReactNode } from 'react'

export type PreviewFeatureType = '0' | '1' | undefined

interface Context {
  isPreviewFeatureEnabled?: PreviewFeatureType
  setIsFeatureEnabled: (nupdatedIsFeatureEnabled?: PreviewFeatureType) => void
}

interface ProviderProps {
  children: ReactNode
  isPreviewFeatureEnabled?: PreviewFeatureType
}

export const PreviewFeatureContext = createContext<Context | undefined>(undefined)

export const PREVIEW_FEATURE_COOKIE_NAME = 'isPreviewFeatureEnabled'

export const PreviewFeatureContextProvider = ({
  children,
  isPreviewFeatureEnabled: isPreviewFeatureEnabledProp
}: ProviderProps) => {
  const cookie = (get(PREVIEW_FEATURE_COOKIE_NAME) || undefined) as PreviewFeatureType

  const [isPreviewFeatureEnabled, setIsPreviewFeatureEnabledState] = useState<PreviewFeatureType>(isPreviewFeatureEnabledProp || cookie)

  const updateCookie = (isFeatureEnabled?: string) => {
    if (isFeatureEnabled) {
      set(PREVIEW_FEATURE_COOKIE_NAME, isFeatureEnabled)
    } else {
      erase(PREVIEW_FEATURE_COOKIE_NAME)
    }
  }

  if (isPreviewFeatureEnabled !== cookie) updateCookie(isPreviewFeatureEnabled)

  const setIsFeatureEnabled = (updatedIsFeatureEnabled?: PreviewFeatureType) => {
    updateCookie(updatedIsFeatureEnabled)
    setIsPreviewFeatureEnabledState(updatedIsFeatureEnabled)
  }

  const value = {
    isPreviewFeatureEnabled,
    setIsFeatureEnabled
  }

  return (
    <PreviewFeatureContext.Provider value={value}>
      {children}
    </PreviewFeatureContext.Provider>
  )
}

interface ConsumerProps {
  children: (context: Context) => ReactNode
}

export const PreviewFeatureContextConsumer = ({
  children: renderChildren
}: ConsumerProps) =>
  <PreviewFeatureContext.Consumer>
    {
      (context?: Context) => {
        if (context === undefined) {
          throw new Error('<PreviewFeatureContextConsumer /> must be used within <PreviewFeatureContextProvider />')
        }

        return renderChildren(context)
      }
    }
  </PreviewFeatureContext.Consumer>
